import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { restaurants } from "@/mocks/restaurants";
import RestaurantReviews from "./components/RestaurantReviews";
import ReservationForm from "./components/ReservationForm";
import WorkingHours from "./components/WorkingHours";
import SimilarRestaurants from "./components/SimilarRestaurants";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageView } from "@/hooks/usePageView";

// City → SSS page mapping for restaurants
const restCityFaqLinks: Record<string, { path: string; label: string }> = {
  "İstanbul": { path: "/istanbul-sss", label: "İstanbul Sık Sorulan Sorular" },
  "Nevşehir": { path: "/kapadokya-sss", label: "Kapadokya Sık Sorulan Sorular" },
  "Antalya": { path: "/antalya-sss", label: "Antalya Sık Sorulan Sorular" },
  "İzmir": { path: "/izmir-sss", label: "İzmir Sık Sorulan Sorular" },
  "Mardin": { path: "/mardin-sss", label: "Mardin Sık Sorulan Sorular" },
  "Rize": { path: "/trabzon-sss", label: "Trabzon & Karadeniz Sık Sorulan Sorular" },
  "Trabzon": { path: "/trabzon-sss", label: "Trabzon Sık Sorulan Sorular" },
  "Gaziantep": { path: "/gaziantep-sss", label: "Gaziantep Sık Sorulan Sorular" },
  "Sakarya": { path: "/sakarya-sss", label: "Sakarya Sık Sorulan Sorular" },
  "Kocaeli": { path: "/kocaeli-sss", label: "Kocaeli Sık Sorulan Sorular" },
  "Bolu": { path: "/bolu-sss", label: "Bolu Sık Sorulan Sorular" },
};

// ── SEO helpers ──────────────────────────────────────────────
const setMetaTag = (name: string, content: string, isProperty = false) => {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLinkTag = (rel: string, href: string) => {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const injectSchema = (id: string, schema: object) => {
  const existing = document.getElementById(`schema-rest-${id}`);
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.id = `schema-rest-${id}`;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
};
// ─────────────────────────────────────────────────────────────

const priceColors: Record<string, string> = {
  "₺": "text-emerald-600",
  "₺₺": "text-amber-600",
  "₺₺₺": "text-orange-600",
  "₺₺₺₺": "text-rose-600",
};

interface LightboxProps {
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onSelect: (i: number) => void;
  name: string;
}

const Lightbox = ({ images, currentIndex, onClose, onPrev, onNext, onSelect, name }: LightboxProps) => (
  <div
    className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center"
    onClick={onClose}
  >
    <button
      className="absolute top-5 right-6 text-white/70 hover:text-white text-3xl cursor-pointer z-10"
      onClick={onClose}
    >
      <i className="ri-close-line"></i>
    </button>
    <div
      className="relative w-full max-w-5xl px-4 flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onPrev}
        className="absolute left-2 md:left-6 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white rounded-full cursor-pointer transition-colors z-10"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <img
        src={images[currentIndex]}
        alt={`${name} - ${currentIndex + 1}`}
        className="max-h-[70vh] w-full object-contain rounded-lg"
      />
      <button
        onClick={onNext}
        className="absolute right-2 md:right-6 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white rounded-full cursor-pointer transition-colors z-10"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </div>
    <div className="flex gap-2 mt-5 px-4 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
      {images.map((img, i) => (
        <button
          key={i}
          onClick={() => onSelect(i)}
          className={`flex-shrink-0 w-14 h-10 rounded overflow-hidden border-2 cursor-pointer transition-all ${
            i === currentIndex ? "border-amber-400 opacity-100" : "border-transparent opacity-50 hover:opacity-80"
          }`}
        >
          <img src={img} alt="" className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
    <p className="text-white/50 text-xs mt-3">
      {currentIndex + 1} / {images.length}
    </p>
  </div>
);

// ── Menu data per restaurant ──────────────────────────────────
const menuData: Record<string, { category: string; items: { name: string; desc: string; price: string }[] }[]> = {
  "ciya-sofrasi": [
    {
      category: "Günlük Spesiyaller",
      items: [
        { name: "Ekşili Etli Yemek", desc: "Günlük değişen Anadolu usulü ekşili et yemeği", price: "₺180" },
        { name: "Nar Ekşili Salata", desc: "Taze sebzeler, nar ekşisi ve zeytinyağı", price: "₺95" },
        { name: "Kuru Meyveli Pilav", desc: "Buharda pişirilmiş, kuru meyve ve baharatlarla", price: "₺120" },
      ],
    },
    {
      category: "Mezeler",
      items: [
        { name: "Anadolu Meze Tabağı", desc: "Günlük hazırlanan 5 çeşit Anadolu mezesi", price: "₺160" },
        { name: "Cacık", desc: "Yoğurt, salatalık, sarımsak ve nane", price: "₺65" },
      ],
    },
    {
      category: "Tatlılar",
      items: [
        { name: "Bölgesel Tatlı Tabağı", desc: "Günlük değişen geleneksel Anadolu tatlıları", price: "₺110" },
      ],
    },
  ],
  "mikla": [
    {
      category: "Degustation Menüsü",
      items: [
        { name: "7 Tabak Degustation", desc: "Şef Mehmet Gürs imzalı mevsimsel yaratıcı menü", price: "₺2.200" },
        { name: "5 Tabak Degustation", desc: "Kısa degustation deneyimi", price: "₺1.600" },
      ],
    },
    {
      category: "À La Carte",
      items: [
        { name: "Anadolu Peynir Tabağı", desc: "Seçilmiş yerel peynirler, bal ve ceviz", price: "₺480" },
        { name: "Karadeniz Alabalığı", desc: "Taze alabalık, yabani otlar ve tereyağı sosu", price: "₺680" },
        { name: "Kuzu Sırtı", desc: "Yavaş pişirilmiş kuzu, kök sebzeler ve jus", price: "₺780" },
      ],
    },
    {
      category: "Çatı Bar",
      items: [
        { name: "İstanbul Sling", desc: "Özel kokteyl, Boğaz manzarası eşliğinde", price: "₺380" },
        { name: "Türk Kahvesi Martini", desc: "Türk kahvesi, votka ve karamel", price: "₺350" },
      ],
    },
  ],
  "imam-cagdas": [
    {
      category: "Tatlılar",
      items: [
        { name: "Fıstıklı Baklava", desc: "Antep fıstığıyla hazırlanan geleneksel baklava", price: "₺120" },
        { name: "Katmer", desc: "Taş fırında pişen, kaymak ve fıstıklı sabah lezzeti", price: "₺95" },
        { name: "Sütlü Nuriye", desc: "Sütlü şerbetli baklava çeşidi", price: "₺110" },
      ],
    },
    {
      category: "Ana Yemekler",
      items: [
        { name: "Antep Kebabı", desc: "Fıstıklı özel harçla hazırlanan Gaziantep usulü kebap", price: "₺280" },
        { name: "Lahmacun", desc: "İnce hamur, kıymalı Antep usulü lahmacun", price: "₺65" },
      ],
    },
    {
      category: "Çorbalar",
      items: [
        { name: "Beyran Çorbası", desc: "Kuzu eti, pirinç ve sarımsaklı geleneksel çorba", price: "₺95" },
        { name: "Mercimek Çorbası", desc: "Kırmızı mercimek, tereyağı ve kırmızı biber", price: "₺75" },
      ],
    },
  ],
  "borsam-tasievi": [
    {
      category: "Başlangıçlar",
      items: [
        { name: "Kibe", desc: "Mardin usulü içli köfte, nar ekşisi ile", price: "₺145" },
        { name: "Mırra Kahvesi", desc: "Geleneksel acı Arap kahvesi, küçük fincan", price: "₺55" },
        { name: "Mezze Tabağı", desc: "Humus, muhammara ve yerel mezeler", price: "₺175" },
      ],
    },
    {
      category: "Ana Yemekler",
      items: [
        { name: "Kuru Soğan Dolması", desc: "Baharatlı iç harçla doldurulmuş soğan dolması", price: "₺195" },
        { name: "Kuzu Güveç", desc: "Yavaş pişirilmiş kuzu, Mardin baharatları", price: "₺285" },
      ],
    },
  ],
  "hatay-sultan-sofrasi": [
    {
      category: "Başlangıçlar",
      items: [
        { name: "Humus", desc: "Nohut ezmesi, zeytinyağı ve kırmızı biber", price: "₺85" },
        { name: "Muhammara", desc: "Kırmızı biber ve ceviz ezmesi", price: "₺90" },
        { name: "Oruk", desc: "Hatay usulü içli köfte", price: "₺120" },
      ],
    },
    {
      category: "Ana Yemekler",
      items: [
        { name: "Hatay Kebabı", desc: "Arap baharatlarıyla marine edilmiş ızgara et", price: "₺245" },
        { name: "Künefe", desc: "Peynirli, şerbetli geleneksel Hatay tatlısı", price: "₺130" },
      ],
    },
  ],
  "karadeniz-pidecisi": [
    {
      category: "Pideler",
      items: [
        { name: "Trabzon Pidesi", desc: "Taş fırında pişen, peynir ve yumurtalı geleneksel pide", price: "₺95" },
        { name: "Kıymalı Pide", desc: "Kıyma, soğan ve baharatlarla", price: "₺110" },
        { name: "Kaşarlı Pide", desc: "Bol kaşar peynirli pide", price: "₺90" },
      ],
    },
    {
      category: "Kahvaltı",
      items: [
        { name: "Kuymak", desc: "Mısır unu, peynir ve tereyağıyla geleneksel Karadeniz kahvaltısı", price: "₺120" },
        { name: "Hamsi Pilavı", desc: "Tereyağlı, taze hamsiyle pişirilmiş pilav (mevsimlik)", price: "₺145" },
      ],
    },
  ],
  "rize-serender": [
    {
      category: "Karadeniz Lezzetleri",
      items: [
        { name: "Muhlama", desc: "Mısır unu, peynir ve tereyağıyla geleneksel yemek", price: "₺110" },
        { name: "Karalahana Çorbası", desc: "Taze karalahana, mısır unu ve et suyu", price: "₺85" },
        { name: "Hamsi Tava", desc: "Taze hamsi, mısır unu ile kızartılmış", price: "₺130" },
      ],
    },
    {
      category: "İçecekler",
      items: [
        { name: "Taze Rize Çayı", desc: "Yöresel çay bahçelerinden taze demleme", price: "₺25" },
      ],
    },
  ],
  "trabzon-cemilusta": [
    {
      category: "Pideler",
      items: [
        { name: "Trabzon Pidesi", desc: "Taş fırında pişen, peynir ve yumurtalı geleneksel pide", price: "₺90" },
        { name: "Kıymalı Pide", desc: "Kıyma ve soğanla", price: "₺105" },
      ],
    },
    {
      category: "Kahvaltı",
      items: [
        { name: "Kuymak", desc: "Mısır unu, peynir ve tereyağıyla geleneksel Karadeniz kahvaltısı", price: "₺115" },
        { name: "Tereyağlı Hamsi", desc: "Taze hamsi, tereyağı ile pişirilmiş", price: "₺125" },
      ],
    },
  ],
  "assk-kahve": [
    {
      category: "Kahvaltı & Brunch",
      items: [
        { name: "Ege Kahvaltısı", desc: "20+ çeşit zeytinyağlı, peynir, bal ve ot tabağı", price: "₺320" },
        { name: "Zeytinyağlı Mezeler", desc: "Günlük hazırlanan 5 çeşit zeytinyağlı", price: "₺195" },
        { name: "Ot Salatası", desc: "Taze Ege otları, limon ve zeytinyağı", price: "₺110" },
      ],
    },
    {
      category: "İçecekler",
      items: [
        { name: "Ev Yapımı Limonata", desc: "Taze sıkılmış, nane ve zencefil ile", price: "₺75" },
        { name: "Türk Kahvesi", desc: "Geleneksel pişirim, lokum ile", price: "₺65" },
      ],
    },
  ],
  "izmir-deniz": [
    {
      category: "Deniz Ürünleri",
      items: [
        { name: "Günlük Taze Balık", desc: "Sabah tutulmuş, ızgara veya buğulama", price: "₺380" },
        { name: "Izgara Ahtapot", desc: "Zeytinyağı, limon ve kekik ile", price: "₺320" },
        { name: "Karides Güveç", desc: "Domates sosu ve baharatlarla", price: "₺295" },
      ],
    },
    {
      category: "Mezeler",
      items: [
        { name: "Zeytinyağlı Mezeler", desc: "Günlük hazırlanan 5 çeşit Ege mezesi", price: "₺185" },
        { name: "Ege Salatası", desc: "Taze sebzeler, zeytin ve beyaz peynir", price: "₺120" },
      ],
    },
  ],
  "antalya-seraser": [
    {
      category: "Degustation",
      items: [
        { name: "Akdeniz Degustasyon Menüsü", desc: "6 tabak, şarap eşleştirmesi ile", price: "₺1.850" },
        { name: "Vejetaryen Degustation", desc: "5 tabak, mevsimsel sebzeler", price: "₺1.450" },
      ],
    },
    {
      category: "À La Carte",
      items: [
        { name: "Taze Deniz Ürünleri", desc: "Günlük taze balık, Akdeniz otları ile", price: "₺680" },
        { name: "Yerel Ot Salatası", desc: "Taze Akdeniz otları, narenciye sosu", price: "₺195" },
        { name: "Kuzu Sırtı", desc: "Yavaş pişirilmiş, kök sebzeler ve jus", price: "₺720" },
      ],
    },
  ],
  "alanya-iskele": [
    {
      category: "Deniz Ürünleri",
      items: [
        { name: "Günlük Taze Balık", desc: "Sabah tutulmuş, ızgara veya buğulama", price: "₺280" },
        { name: "Izgara Ahtapot", desc: "Zeytinyağı ve limon ile", price: "₺245" },
        { name: "Midye Tava", desc: "Taze midye, sarımsaklı tereyağı ile", price: "₺185" },
      ],
    },
    {
      category: "Mezeler",
      items: [
        { name: "Akdeniz Mezeleri", desc: "5 çeşit taze meze tabağı", price: "₺165" },
      ],
    },
  ],
  "safranbolu-havuzlu": [
    {
      category: "Kahvaltı",
      items: [
        { name: "Osmanlı Kahvaltısı", desc: "Safran çayı, ev yapımı reçeller, peynir ve bal", price: "₺220" },
        { name: "Safran Çayı", desc: "Geleneksel safran demlemesi", price: "₺45" },
        { name: "Tahin Helvası", desc: "El yapımı geleneksel helva", price: "₺75" },
      ],
    },
    {
      category: "Öğle Yemeği",
      items: [
        { name: "Karadeniz Çorbası", desc: "Günlük hazırlanan geleneksel çorba", price: "₺85" },
        { name: "Safranbolu Güveci", desc: "Yöresel baharatlarla hazırlanan et güveci", price: "₺195" },
      ],
    },
  ],
  "bolu-abant": [
    {
      category: "Ana Yemekler",
      items: [
        { name: "Taze Alabalık", desc: "Abant gölünden taze, ızgara veya buğulama", price: "₺195" },
        { name: "Mantar Kavurması", desc: "Orman mantarları, tereyağı ve sarımsak", price: "₺165" },
        { name: "Bolu Tereyağlı Yufka", desc: "El açması yufka, Bolu tereyağı ile", price: "₺120" },
      ],
    },
    {
      category: "Kahvaltı",
      items: [
        { name: "Orman Kahvaltısı", desc: "Yerel ürünler, bal, kaymak ve peynir", price: "₺185" },
      ],
    },
  ],
  "konya-sifa": [
    {
      category: "Ana Yemekler",
      items: [
        { name: "Fırın Kebabı", desc: "Yavaş pişirilmiş kuzu, Konya usulü", price: "₺195" },
        { name: "Etli Ekmek", desc: "Kıymalı, ince hamurlu Konya pidesi", price: "₺85" },
        { name: "Tirit", desc: "Et suyu ve ekmekle hazırlanan geleneksel yemek", price: "₺155" },
      ],
    },
    {
      category: "Çorbalar",
      items: [
        { name: "Bamya Çorbası", desc: "Kuzu eti ve bamya ile geleneksel çorba", price: "₺80" },
        { name: "Mercimek Çorbası", desc: "Kırmızı mercimek, tereyağı ve kırmızı biber", price: "₺70" },
      ],
    },
  ],
  "ankara-kizilirmak": [
    {
      category: "Ana Yemekler",
      items: [
        { name: "Testi Kebabı", desc: "Kil testide pişirilmiş, masa başında kırılan kebap", price: "₺245" },
        { name: "Beypazarı Güveci", desc: "Yöresel sebzeler ve et ile güveç", price: "₺215" },
        { name: "Ankara Tava", desc: "Kuzu eti, domates ve biber ile", price: "₺195" },
      ],
    },
    {
      category: "Tatlılar",
      items: [
        { name: "Höşmerim", desc: "Peynirli geleneksel Ankara tatlısı", price: "₺85" },
      ],
    },
  ],
};

// ── Map coordinates per restaurant ───────────────────────────
const mapCoords: Record<string, { lat: number; lng: number; zoom: number }> = {
  "ciya-sofrasi": { lat: 40.9906, lng: 29.0293, zoom: 16 },
  "mikla": { lat: 41.0338, lng: 28.9773, zoom: 16 },
  "imam-cagdas": { lat: 37.0662, lng: 37.3833, zoom: 16 },
  "borsam-tasievi": { lat: 37.3126, lng: 40.7350, zoom: 16 },
  "hatay-sultan-sofrasi": { lat: 36.2021, lng: 36.1603, zoom: 16 },
  "karadeniz-pidecisi": { lat: 41.0015, lng: 39.7178, zoom: 15 },
  "rize-serender": { lat: 40.9200, lng: 40.9200, zoom: 14 },
  "trabzon-cemilusta": { lat: 41.0015, lng: 39.7178, zoom: 15 },
  "assk-kahve": { lat: 38.2833, lng: 26.3833, zoom: 16 },
  "izmir-deniz": { lat: 38.4192, lng: 27.1287, zoom: 16 },
  "antalya-seraser": { lat: 36.8841, lng: 30.7056, zoom: 16 },
  "alanya-iskele": { lat: 36.5440, lng: 32.0000, zoom: 15 },
  "safranbolu-havuzlu": { lat: 41.2500, lng: 32.6833, zoom: 16 },
  "bolu-abant": { lat: 40.6000, lng: 31.2833, zoom: 14 },
  "konya-sifa": { lat: 37.8667, lng: 32.4833, zoom: 15 },
  "ankara-kizilirmak": { lat: 39.9334, lng: 32.8597, zoom: 15 },
};

const RestaurantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Check both mock data and localStorage approved restaurants
  const approvedFromStorage: (typeof restaurants[0] & { ownerSubmitted?: boolean })[] = JSON.parse(
    localStorage.getItem("approvedRestaurants") || "[]"
  );
  const allRestaurants = [...restaurants, ...approvedFromStorage];
  const rest = allRestaurants.find((r) => r.id === id) as (typeof restaurants[0] & { ownerSubmitted?: boolean }) | undefined;

  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeMenuCat, setActiveMenuCat] = useState(0);

  const allImages = rest ? (rest.images && rest.images.length > 0 ? rest.images : [rest.image]) : [];
  const menu = id ? (menuData[id] || null) : null;
  const coords = id ? (mapCoords[id] || null) : null;
  const viewCount = usePageView(id || "", "restaurant");

  const handlePrev = useCallback(() => {
    setActiveImg((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  }, [allImages.length]);

  const handleNext = useCallback(() => {
    setActiveImg((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  }, [allImages.length]);

  // ── SEO ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!rest || !id) return;

    const pageTitle = `${rest.name} — ${rest.city} ${rest.cuisine} Restoranı | Türkiye Seyahat`;
    const pageDesc = `${rest.name}, ${rest.city} ${rest.district} bölgesinde ${rest.cuisine} mutfağı sunan bir restoran. Puan: ${rest.rating}/5. ${rest.shortDesc || ""}`.slice(0, 160);
    const pageKeywords = `${rest.name}, ${rest.city} restoranlar, ${rest.cuisine} restoran, ${rest.district} yemek, ${rest.tags?.join(", ") || ""}`;
    const canonicalUrl = `https://geziturkiyem.com/restaurant/${id}`;
    const allImgs = rest.images && rest.images.length > 0 ? rest.images : [rest.image];

    document.title = pageTitle;
    setMetaTag("description", pageDesc);
    setMetaTag("keywords", pageKeywords);
    setMetaTag("robots", "index, follow");
    setLinkTag("canonical", canonicalUrl);

    // Open Graph
    setMetaTag("og:title", pageTitle, true);
    setMetaTag("og:description", pageDesc, true);
    setMetaTag("og:image", rest.image, true);
    setMetaTag("og:image:width", "800", true);
    setMetaTag("og:image:height", "450", true);
    setMetaTag("og:image:alt", rest.name, true);
    setMetaTag("og:image:type", "image/jpeg", true);
    setMetaTag("og:url", canonicalUrl, true);
    setMetaTag("og:type", "restaurant.restaurant", true);
    setMetaTag("og:locale", "tr_TR", true);
    setMetaTag("og:site_name", "Yerel Bilgi — Türkiye Seyahat Rehberi", true);

    // Twitter Card
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:site", "@geziturkiyem");
    setMetaTag("twitter:title", pageTitle);
    setMetaTag("twitter:description", pageDesc);
    setMetaTag("twitter:image", rest.image);
    setMetaTag("twitter:image:alt", rest.name);

    // Geo tags
    setMetaTag("geo.placename", rest.city);
    setMetaTag("geo.region", "TR");

    // Schema.org Restaurant (LocalBusiness)
    const coordsData = id ? (mapCoords[id] || null) : null;
    const menuItems = id ? (menuData[id] || null) : null;

    const schemaData: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Restaurant",
      "name": rest.name,
      "description": pageDesc,
      "image": allImgs.map((img) => ({
        "@type": "ImageObject",
        "url": img,
        "width": 800,
        "height": 450,
      })),
      "url": canonicalUrl,
      "servesCuisine": rest.cuisine,
      "priceRange": rest.priceRange,
      "currenciesAccepted": "TRY",
      "paymentAccepted": "Cash, Credit Card",
      "inLanguage": "tr-TR",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": rest.city,
        "streetAddress": rest.district,
        "addressRegion": rest.region,
        "addressCountry": "TR",
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": rest.rating,
        "bestRating": 5,
        "worstRating": 1,
        "ratingCount": 95,
      },
      "publisher": {
        "@type": "Organization",
        "name": "Yerel Bilgi — Türkiye Seyahat Rehberi",
        "url": "https://geziturkiyem.com",
      },
    };

    if (coordsData) {
      schemaData["geo"] = {
        "@type": "GeoCoordinates",
        "latitude": coordsData.lat,
        "longitude": coordsData.lng,
      };
      schemaData["hasMap"] = `https://www.google.com/maps?q=${coordsData.lat},${coordsData.lng}`;
    }

    if (menuItems && menuItems.length > 0) {
      schemaData["hasMenu"] = {
        "@type": "Menu",
        "name": `${rest.name} Menüsü`,
        "hasMenuSection": menuItems.map((cat) => ({
          "@type": "MenuSection",
          "name": cat.category,
          "hasMenuItem": cat.items.map((item) => ({
            "@type": "MenuItem",
            "name": item.name,
            "description": item.desc,
            "offers": {
              "@type": "Offer",
              "price": item.price.replace("₺", "").replace(/\s/g, ""),
              "priceCurrency": "TRY",
            },
          })),
        })),
      };
    }

    injectSchema(id, schemaData);

    // BreadcrumbList
    const breadcrumbId = `schema-bc-rest-${id}`;
    document.getElementById(breadcrumbId)?.remove();
    const bcScript = document.createElement("script");
    bcScript.id = breadcrumbId;
    bcScript.type = "application/ld+json";
    bcScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": "https://geziturkiyem.com/" },
        { "@type": "ListItem", "position": 2, "name": "Restoranlar", "item": "https://geziturkiyem.com/restoranlar" },
        { "@type": "ListItem", "position": 3, "name": rest.name, "item": canonicalUrl },
      ],
    });
    document.head.appendChild(bcScript);

    return () => {
      document.title = "Türkiye Seyahat Rehberi";
      document.getElementById(`schema-rest-${id}`)?.remove();
      document.getElementById(`schema-bc-rest-${id}`)?.remove();
    };
  }, [id, rest]);
  // ─────────────────────────────────────────────────────────────

  if (!rest) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-stone-400 mb-4">Restoran bulunamadı.</p>
          <button
            onClick={() => navigate("/restoranlar")}
            className="text-amber-600 font-medium cursor-pointer"
          >
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Image with Gallery */}
      <div className="relative h-[55vh] overflow-hidden">
        <img
          src={allImages[activeImg]}
          alt={rest.name}
          className="w-full h-full object-cover object-top transition-all duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

        {/* Back Button */}
        <button
          onClick={() => navigate("/restoranlar")}
          className="absolute top-24 left-6 flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm px-4 py-2 rounded-full cursor-pointer hover:bg-white/30 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i> Geri
        </button>

        {/* Expand Button */}
        <button
          onClick={() => setLightboxOpen(true)}
          className="absolute top-24 right-6 flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm px-4 py-2 rounded-full cursor-pointer hover:bg-white/30 transition-colors whitespace-nowrap"
        >
          <i className="ri-fullscreen-line"></i>
          <span className="hidden sm:inline">Tüm Fotoğraflar</span>
          <span className="sm:hidden">{allImages.length}</span>
        </button>

        {/* Prev / Next Arrows */}
        {allImages.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/30 hover:bg-black/50 text-white rounded-full cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/30 hover:bg-black/50 text-white rounded-full cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </>
        )}

        {/* Bottom Info */}
        <div className="absolute bottom-8 left-0 right-0 px-6 md:px-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="bg-white/20 backdrop-blur-sm border border-white/30 text-white text-xs px-3 py-1 rounded-full">
                {rest.cuisine}
              </span>
              {rest.ownerSubmitted && (
                <span className="flex items-center gap-1.5 bg-amber-500/90 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full font-medium">
                  <i className="ri-store-2-line text-xs"></i>
                  Sahip Başvurusuyla Eklendi
                </span>
              )}
            </div>
            <h1
              className="text-4xl md:text-5xl font-bold text-white mt-1 mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {rest.name}
            </h1>
            <div className="flex items-center gap-4 text-white/80 text-sm flex-wrap">
              <div className="flex items-center gap-1">
                <i className="ri-map-pin-line text-amber-400"></i>
                <span>{rest.city}, {rest.district}</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-star-fill text-amber-400"></i>
                <span>{rest.rating} / 5</span>
              </div>
              <span className={`font-bold text-lg ${priceColors[rest.priceRange]}`}>
                {rest.priceRange}
              </span>
              {viewCount > 0 && (
                <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/20 px-3 py-1 rounded-full">
                  <i className="ri-eye-line text-amber-300 text-xs"></i>
                  <span className="text-white text-xs font-semibold">
                    {viewCount.toLocaleString("tr-TR")} görüntülenme
                  </span>
                </div>
              )}
              <a
                href="#rezervasyon"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("rezervasyon-section")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-white text-xs font-semibold px-4 py-1.5 rounded-full cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-calendar-check-line"></i>
                Rezervasyon Yap
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Thumbnail Strip */}
      {allImages.length > 1 && (
        <div className="bg-stone-50 border-b border-stone-100 px-4 md:px-16 py-3">
          <div className="max-w-4xl mx-auto flex gap-2 overflow-x-auto">
            {allImages.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`flex-shrink-0 w-16 h-11 rounded-md overflow-hidden border-2 cursor-pointer transition-all ${
                  i === activeImg
                    ? "border-amber-500 opacity-100"
                    : "border-transparent opacity-60 hover:opacity-90"
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        {/* Owner Submitted Banner */}
        {rest.ownerSubmitted && (
          <div className="mb-8 flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
              <i className="ri-store-2-line text-amber-600 text-sm"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-800">Bu restoran sahip başvurusuyla eklendi</p>
              <p className="text-xs text-amber-600 mt-0.5">
                Bu restoran, işletme sahibinin başvurusu ve editörlerimizin onayı ile Türkiye Seyahat Rehberi'ne eklenmiştir.
              </p>
            </div>
          </div>
        )}

        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: "Ana Sayfa", href: "/" },
            { label: "Restoranlar", href: "/restoranlar" },
            { label: rest.city },
            { label: rest.name },
          ]}
          className="mb-8 text-stone-400"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Description */}
          <div className="md:col-span-2">
            <h2
              className="text-xl font-bold text-stone-900 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Hakkında
            </h2>
            <div className="text-stone-600 text-sm leading-relaxed space-y-4">
              {rest.description.split("\n\n").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-6">
              {rest.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-amber-50 text-amber-700 text-xs px-3 py-1 rounded-full border border-amber-100"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            <div className="bg-amber-50 rounded-2xl p-5">
              <h3 className="font-bold text-stone-900 text-sm mb-3">Mutlaka Deneyin</h3>
              <ul className="space-y-2">
                {rest.mustTry.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-stone-600">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-restaurant-fill text-amber-500 text-xs"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-stone-50 rounded-2xl p-5">
              <h3 className="font-bold text-stone-900 text-sm mb-3">Bilgiler</h3>
              <div className="space-y-2 text-sm text-stone-600">
                <div className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-amber-500"></i>
                  <span>{rest.city}, {rest.district}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-restaurant-line text-amber-500"></i>
                  <span>{rest.cuisine}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-money-cny-circle-line text-amber-500"></i>
                  <span className={priceColors[rest.priceRange]}>{rest.priceRange}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-star-fill text-amber-500"></i>
                  <span>{rest.rating} / 5 puan</span>
                </div>
              </div>
            </div>

            {/* View Counter Card */}
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-xl">
                  <i className="ri-eye-line text-amber-600 text-lg"></i>
                </div>
                <div>
                  <p className="font-bold text-stone-900 text-sm">Ziyaretçi Sayacı</p>
                  <p className="text-stone-400 text-xs">Bu sayfaya bakıldı</p>
                </div>
              </div>
              <p className="text-3xl font-bold text-amber-600" style={{ fontFamily: "'Playfair Display', serif" }}>
                {viewCount > 0 ? viewCount.toLocaleString("tr-TR") : "—"}
              </p>
              <p className="text-xs text-stone-400 mt-1">toplam görüntülenme</p>
              <div className="mt-3 pt-3 border-t border-amber-100">
                <div className="flex items-center gap-1.5 text-xs text-stone-500">
                  <i className="ri-bar-chart-line text-amber-500"></i>
                  <span>Gerçek zamanlı takip</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Reviews */}
        <RestaurantReviews restaurantId={rest.id} restaurantName={rest.name} />

        {/* ── Menu Section ── */}
        {menu && (
          <div className="mt-14">
            <h2
              className="text-xl font-bold text-stone-900 mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Menü
            </h2>
            {/* Category Tabs */}
            <div className="flex gap-2 flex-wrap mb-6">
              {menu.map((cat, i) => (
                <button
                  key={cat.category}
                  onClick={() => setActiveMenuCat(i)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    activeMenuCat === i
                      ? "bg-amber-500 text-white"
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                >
                  {cat.category}
                </button>
              ))}
            </div>
            {/* Menu Items */}
            <div className="space-y-3">
              {menu[activeMenuCat]?.items.map((item) => (
                <div
                  key={item.name}
                  className="flex items-start justify-between gap-4 p-4 bg-stone-50 rounded-xl hover:bg-amber-50 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-semibold text-stone-900 text-sm">{item.name}</p>
                    <p className="text-stone-500 text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                  <span className="text-amber-600 font-bold text-sm whitespace-nowrap flex-shrink-0">{item.price}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-stone-400 mt-4 italic">
              * Fiyatlar ve menü içeriği değişebilir. Güncel bilgi için restoranı arayınız.
            </p>
          </div>
        )}

        {/* ── Working Hours ── */}
        <WorkingHours restaurantId={rest.id} restaurantName={rest.name} />

        {/* ── Map Section ── */}
        {coords && (
          <div className="mt-14">
            <h2
              className="text-xl font-bold text-stone-900 mb-5"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Konum
            </h2>
            <div className="rounded-2xl overflow-hidden border border-stone-100" style={{ height: "340px" }}>
              <iframe
                title={`${rest.name} harita`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(rest.name + " " + rest.city + " " + rest.district)}&z=${coords.zoom}&output=embed`}
              ></iframe>
            </div>
            <div className="flex items-center gap-2 mt-3 text-sm text-stone-500">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-line text-amber-500"></i>
              </div>
              <span>{rest.city}, {rest.district}</span>
              <a
                href={`https://www.google.com/maps/search/${encodeURIComponent(rest.name + " " + rest.city)}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="ml-auto text-amber-600 hover:text-amber-700 text-xs font-medium cursor-pointer whitespace-nowrap"
              >
                Google Maps'te Aç <i className="ri-external-link-line"></i>
              </a>
            </div>
          </div>
        )}

        {/* City FAQ Internal Link Box */}
        {(() => {
          const faqLink = rest.city ? restCityFaqLinks[rest.city] : null;
          if (!faqLink) return null;
          return (
            <div className="mt-10 p-5 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                <i className="ri-question-answer-line text-amber-600 text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">Merak Ettikleriniz</p>
                <p className="text-stone-700 text-sm mb-3 leading-relaxed">
                  {rest.city} hakkında sık sorulan soruları ve pratik seyahat bilgilerini derledik.
                </p>
                <Link
                  to={faqLink.path}
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-right-circle-line"></i>
                  {faqLink.label}
                </Link>
              </div>
            </div>
          );
        })()}

        {/* ── Similar Restaurants ── */}
        <SimilarRestaurants
          currentId={rest.id}
          region={rest.region}
          cuisine={rest.cuisine}
        />

        {/* ── Reservation Form ── */}
        <div id="rezervasyon-section">
          <ReservationForm restaurantName={rest.name} restaurantCity={rest.city} />
        </div>

        {/* Photo Gallery Grid */}
        {allImages.length > 1 && (
          <div className="mt-12">
            <h2
              className="text-xl font-bold text-stone-900 mb-5"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Fotoğraf Galerisi
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {allImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => { setActiveImg(i); setLightboxOpen(true); }}
                  className={`relative overflow-hidden rounded-xl cursor-pointer group ${
                    i === 0 ? "col-span-2 row-span-2 h-64 md:h-72" : "h-32 md:h-36"
                  }`}
                >
                  <img
                    src={img}
                    alt={`${rest.name} - ${i + 1}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-zoom-in-line text-white text-xl"></i>
                    </div>
                  </div>
                  {i === allImages.length - 1 && allImages.length > 5 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white font-bold text-lg">+{allImages.length - 5}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={allImages}
          currentIndex={activeImg}
          onClose={() => setLightboxOpen(false)}
          onPrev={handlePrev}
          onNext={handleNext}
          onSelect={setActiveImg}
          name={rest.name}
        />
      )}

      <Footer />
    </div>
  );
};

export default RestaurantDetail;
